"use client";

import { useState } from "react";
import { createPortal } from "react-dom";
import PhotoLightbox from "@/components/result/PhotoLightbox";
import Photo from "@/components/Photo";
import { useT } from "@/lib/locale-context";

/**
 * Outside shots of a residence, picked from its listings' photos by the
 * photo-intel scene model and reviewed by hand
 * (scripts/build_residence_galleries.py). The pages that outrank ours
 * for a residence's name lead with 8 to 22 photos of the building
 * (Google review, 2026-10-13); this is ours.
 *
 * Phones swipe a row of tiles; from sm up four tiles sit in a grid and
 * the last one carries the count of the rest. Every tile opens the
 * shared full-screen viewer. bina's card size paints the tiles, the
 * viewer loads the original.
 */

const card = (u: string) => u.replace(/\/uploads\/(thumbnail|full)\//, "/uploads/f460x345/");
/** The original-size file of a gallery photo (the viewer loads it). */
export const fullPhoto = (u: string) => u.replace(/\/uploads\/(thumbnail|f460x345)\//, "/uploads/full/");
const full = fullPhoto;

export default function ResidenceGallery({
  photos, name, onOpen, className = "mt-6",
}: {
  /** A caption says what the photo shows (the researched profiles'
   *  photos); the rest are the listings' outside shots. */
  photos: { url: string; caption?: string | null }[];
  name: string;
  /** Open the page's own viewer at this photo instead of the built-in one
   *  (the residence page shares one viewer between the top photo and
   *  these tiles). */
  onOpen?: (index: number) => void;
  /** The section's spacing (the residence page passes its own). */
  className?: string;
}) {
  const t = useT();
  const [open, setOpen] = useState<number | null>(null);
  if (photos.length < 2) return null;
  const desktopShown = 4;
  const rest = photos.length - desktopShown;
  const alt = (i: number) => photos[i].caption
    ? `${name}: ${photos[i].caption}`
    : t("residence.gallery.alt").replace("{name}", name).replace("{i}", String(i + 1)).replace("{n}", String(photos.length));
  const captioned = photos.some((p) => p.caption);

  return (
    <section id="foto" className={`${className} scroll-mt-24`} aria-labelledby="foto-h">
      {/* The searched words: "<name> şəkilləri" (queries ask for "foto"). */}
      <h2 id="foto-h" className="text-lg font-bold tracking-tight text-gray-900">{t("residence.gallery.title").replace("{name}", name)}</h2>
      <div className="-mx-4 mt-3 flex snap-x snap-mandatory gap-2 overflow-x-auto px-4 pb-1 sm:mx-0 sm:grid sm:grid-cols-4 sm:overflow-visible sm:px-0 sm:pb-0">
        {photos.map((p, i) => (
          <figure
            key={p.url}
            className={`w-[72%] shrink-0 snap-start sm:w-auto ${i >= desktopShown ? "sm:hidden" : ""}`}
          >
          <button
            type="button"
            onClick={() => (onOpen ? onOpen(i) : setOpen(i))}
            aria-label={alt(i)}
            className="relative block aspect-[4/3] w-full cursor-zoom-in overflow-hidden rounded-xl bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
          >
            <Photo src={card(p.url)} fallbackSrc={full(p.url)} alt={alt(i)} className="object-cover hover:opacity-90" />
            {i === desktopShown - 1 && rest > 0 && (
              <span className="absolute inset-0 hidden items-center justify-center bg-black/55 text-lg font-bold tabular-nums text-white sm:flex">
                +{rest}
              </span>
            )}
          </button>
          {captioned && (
            <figcaption className="mt-1.5 line-clamp-2 text-xs leading-snug text-gray-600">{p.caption ?? ""}</figcaption>
          )}
          </figure>
        ))}
      </div>
      <p className="mt-2 text-xs text-gray-500">{t("residence.gallery.note")}</p>
      {open != null && createPortal(
        <PhotoLightbox photos={photos.map((p) => ({ url: full(p.url), label: p.caption ?? null }))} initialIndex={open} onClose={() => setOpen(null)} />,
        document.body,
      )}
    </section>
  );
}
